import React, { useState, useEffect, useReducer, useCallback } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  Badge,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  DialogActions
} from "@material-ui/core";

import {
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  FilterList as FilterListIcon,
  CloudDownload as CloudDownloadIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ClienteFilterModal from "../../components/ClienteFilterModal";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  clienteCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    cursor: "pointer",
    transition: "all 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4],
    },
  },
  clienteHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  clienteInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  importDialog: {
    "& .MuiDialog-paper": {
      minWidth: "400px",
      padding: theme.spacing(2),
    },
  },
  importContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
  },
  importText: {
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CLIENTES") {
    const clientes = action.payload;
    const newClientes = [];

    clientes.forEach((cliente) => {
      const clienteIndex = state.findIndex((c) => c.CODCLI === cliente.CODCLI);
      if (clienteIndex !== -1) {
        state[clienteIndex] = cliente;
      } else {
        newClientes.push(cliente);
      }
    });

    return [...state, ...newClientes];
  }

  if (action.type === "UPDATE_CLIENTE") {
    const cliente = action.payload;
    const clienteIndex = state.findIndex((c) => c.CODCLI === cliente.CODCLI);

    if (clienteIndex !== -1) {
      state[clienteIndex] = cliente;
      return [...state];
    } else {
      return [cliente, ...state];
    }
  }

  if (action.type === "DELETE_CLIENTE") {
    const clienteId = action.payload;

    const clienteIndex = state.findIndex((c) => c.CODCLI === clienteId);
    if (clienteIndex !== -1) {
      state.splice(clienteIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Clientes = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [clientes, dispatch] = useReducer(reducer, []);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const [totalClientes, setTotalClientes] = useState(0);
  const [importando, setImportando] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importProgress, setImportProgress] = useState({
    phase: 'init',
    message: 'Iniciando...',
    totalClientes: 0,
    clientesProcessados: 0,
    importados: 0,
    atualizados: 0,
    erros: 0,
    percentual: 0
  });
  const [eventSource, setEventSource] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [settings, setSettings] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        console.error("Erro ao buscar configurações:", err);
      }
    };
    fetchSettings();
  }, []);

  const getSettingValue = (key) => {
    const setting = settings.find((s) => s.key === key);
    return setting ? setting.value : "enabled";
  };

  const isClientImportEnabled = () => {
    return getSettingValue("clientImport") === "enabled";
  };

  const handleCancelarImportacao = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    setImportando(false);
    setImportDialogOpen(false);
    toast.info("Importação cancelada. Os clientes já processados foram salvos.");
    
    // Recarrega a lista
    dispatch({ type: "RESET" });
    setPageNumber(1);
    fetchClientes();
  };

  const handleImportarClientes = async () => {
    if (!window.confirm("Deseja importar os clientes do banco de dados externo? Esta operação pode demorar alguns minutos.")) {
      return;
    }

    setImportando(true);
    setImportDialogOpen(true);
    setImportProgress({
      phase: 'init',
      message: 'Iniciando...',
      totalClientes: 0,
      clientesProcessados: 0,
      importados: 0,
      atualizados: 0,
      erros: 0,
      percentual: 0
    });

    const controller = new AbortController();
    setAbortController(controller);

    try {
      const token = localStorage.getItem("token");
      
      // Usa fetch com stream para receber progresso em tempo real
      const response = await api.post("/clientes/importar", {}, {
        responseType: 'stream',
        signal: controller.signal,
        headers: {
          'Accept': 'text/event-stream'
        },
        onDownloadProgress: (progressEvent) => {
          // Processa os eventos SSE do stream
          const text = progressEvent.event.target.responseText;
          const lines = text.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.substring(6));
                
                if (data.type === 'progress') {
                  setImportProgress({
                    phase: data.phase,
                    message: data.message || 'Processando...',
                    totalClientes: data.totalClientes || 0,
                    clientesProcessados: data.clientesProcessados || 0,
                    importados: data.importados || 0,
                    atualizados: data.atualizados || 0,
                    erros: data.erros || 0,
                    percentual: data.percentual || 0
                  });
                } else if (data.type === 'complete') {
                  setImportando(false);
                  setImportDialogOpen(false);
                  setAbortController(null);
                  
                  const result = data.data;
                  toast.success(
                    `Importação concluída! 
                    ${result.importados} cliente(s) importado(s), 
                    ${result.atualizados} atualizado(s), 
                    ${result.erros} erro(s).
                    Total de ${result.totalClientes || 0} clientes processados.`
                  );
                  
                  // Recarrega a lista
                  dispatch({ type: "RESET" });
                  setPageNumber(1);
                  fetchClientes();
                } else if (data.type === 'error') {
                  setImportando(false);
                  setImportDialogOpen(false);
                  setAbortController(null);
                  toast.error(data.message || "Erro ao importar clientes");
                } else if (data.type === 'cancelled') {
                  setImportando(false);
                  setImportDialogOpen(false);
                  setAbortController(null);
                  toast.info("Importação cancelada. Os clientes já processados foram salvos.");
                  
                  // Recarrega a lista
                  dispatch({ type: "RESET" });
                  setPageNumber(1);
                  fetchClientes();
                }
              } catch (err) {
                console.error("Erro ao processar evento SSE:", err);
              }
            }
          }
        }
      });
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        toast.info("Importação cancelada. Os clientes já processados foram salvos.");
        // Recarrega a lista
        dispatch({ type: "RESET" });
        setPageNumber(1);
        fetchClientes();
      } else {
        console.error("Erro ao importar:", err);
        toastError(err);
      }
    } finally {
      // Garante limpeza
      setAbortController(null);
      setImportando(false);
      setImportDialogOpen(false);
    }
  };

  const fetchClientes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        searchParam,
        pageNumber
      };

      // Adicionar filtros se existirem
      if (activeFilters.length > 0) {
        params.filters = JSON.stringify(activeFilters);
      }

      const { data } = await api.get("/clientes", { params });
      dispatch({ type: "LOAD_CLIENTES", payload: data.clientes });
      setHasMore(data.hasMore);
      setTotalClientes(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  }, [searchParam, pageNumber, activeFilters]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, activeFilters]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    if (filters.length > 0) {
      toast.success(`${filters.length} filtro(s) aplicado(s)`);
    }
  };

  const handleOpenFilterModal = () => {
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
  };

  const handleOpenClienteDetails = (cliente) => {
    history.push(`/clientes/${cliente.CODCLI}`);
  };

  const handleDeleteCliente = async (clienteId) => {
    try {
      await api.delete(`/clientes/${clienteId}`);
      toast.success("Cliente deletado com sucesso");
      dispatch({ type: "DELETE_CLIENTE", payload: clienteId });
    } catch (err) {
      toastError(err);
    }
    setSelectedCliente(null);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Deletar Cliente"
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCliente(selectedCliente.CODCLI)}
      >
        Tem certeza que deseja deletar o cliente {selectedCliente?.NOMECLI}?
      </ConfirmationModal>

      <ClienteFilterModal
        open={filterModalOpen}
        onClose={handleCloseFilterModal}
        onApplyFilters={handleApplyFilters}
        currentFilters={activeFilters}
      />

      <MainHeader>
        <Title>
          {totalClientes > 0 
            ? `Mostrando ${clientes.length} de ${totalClientes.toLocaleString('pt-BR')}` 
            : `Clientes (${clientes.length})`}
        </Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Buscar clientes..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Filtros Avançados">
            <IconButton onClick={handleOpenFilterModal} color="primary">
              <Badge badgeContent={activeFilters.length} color="secondary">
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => history.push("/cliente-report")}
            startIcon={<AssessmentIcon />}
          >
            Relatório
          </Button>
          {isClientImportEnabled() && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleImportarClientes}
              disabled={importando}
              startIcon={importando ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
            >
              {importando ? "Importando..." : "Importar Clientes"}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push("/clientes/novo")}
            startIcon={<AddIcon />}
          >
            Novo Cliente
          </Button>
          <IconButton onClick={fetchClientes}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        {clientes.length === 0 && !loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="400px"
          >
            <Typography variant="h6" color="textSecondary">
              Nenhum cliente encontrado
            </Typography>
          </Box>
        ) : (
          <>
            {clientes.map((cliente) => (
              <Paper
                key={cliente.CODCLI}
                className={classes.clienteCard}
                elevation={2}
              >
                <div className={classes.clienteHeader}>
                  <div
                    style={{ flex: 1, cursor: "pointer" }}
                    onClick={() => handleOpenClienteDetails(cliente)}
                  >
                    <Typography variant="h6" color="primary">
                      <BusinessIcon
                        fontSize="small"
                        style={{ verticalAlign: "middle", marginRight: 8 }}
                      />
                      {cliente.NOMECLI}
                    </Typography>
                    {cliente.NOMEFANTASIA && cliente.NOMEFANTASIA !== cliente.NOMECLI && (
                      <Typography variant="body2" color="textSecondary">
                        {cliente.NOMEFANTASIA}
                      </Typography>
                    )}
                  </div>

                  <Box display="flex" gap={1}>
                    <Tooltip title="Ver Resumo">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => history.push(`/clientes/${cliente.CODCLI}/resumo`)}
                      >
                        <DescriptionIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar Cliente">
                      <IconButton
                        size="small"
                        onClick={() => history.push(`/clientes/${cliente.CODCLI}`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {cliente.CELULAR && (
                      <Tooltip title="WhatsApp">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            window.open(
                              `https://wa.me/55${cliente.CELULAR.replace(/\D/g, "")}`,
                              "_blank"
                            )
                          }
                        >
                          <WhatsAppIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Deletar Cliente">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedCliente(cliente);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </div>

                <div className={classes.clienteInfo}>
                  {cliente.CGCCLI && (
                    <div className={classes.infoRow}>
                      <BusinessIcon fontSize="small" />
                      <Typography variant="body2">
                        CNPJ: {cliente.CGCCLI}
                      </Typography>
                    </div>
                  )}

                  {cliente.FONECLI && (
                    <div className={classes.infoRow}>
                      <PhoneIcon fontSize="small" />
                      <Typography variant="body2">
                        Tel: {cliente.FONECLI}
                      </Typography>
                    </div>
                  )}

                  {cliente.E_MAIL && (
                    <div className={classes.infoRow}>
                      <EmailIcon fontSize="small" />
                      <Typography variant="body2">{cliente.E_MAIL}</Typography>
                    </div>
                  )}

                  {(cliente.CIDADECLI || cliente.ESTADOCLI) && (
                    <div className={classes.infoRow}>
                      <LocationIcon fontSize="small" />
                      <Typography variant="body2">
                        {cliente.CIDADECLI}
                        {cliente.CIDADECLI && cliente.ESTADOCLI && " - "}
                        {cliente.ESTADOCLI}
                      </Typography>
                    </div>
                  )}
                </div>
              </Paper>
            ))}
            {loading && (
              <Box display="flex" justifyContent="center" padding={3}>
                <CircularProgress />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Dialog de Importação com Progresso */}
      <Dialog
        open={importDialogOpen}
        className={classes.importDialog}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" align="center">
            Importando Clientes
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className={classes.importContent}>
            <Box width="100%" mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary">
                  {importProgress.message}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {importProgress.percentual}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={importProgress.percentual} 
              />
            </Box>
            
            {importProgress.totalClientes > 0 && (
              <Box width="100%" mb={2}>
                <Typography variant="body2" align="center" gutterBottom>
                  <strong>{importProgress.clientesProcessados}</strong> de{" "}
                  <strong>{importProgress.totalClientes}</strong> clientes processados
                </Typography>
                <Box display="flex" justifyContent="space-around" mt={2}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="primary">
                      {importProgress.importados}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Importados
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h6" color="secondary">
                      {importProgress.atualizados}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Atualizados
                    </Typography>
                  </Box>
                  {importProgress.erros > 0 && (
                    <Box textAlign="center">
                      <Typography variant="h6" color="error">
                        {importProgress.erros}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Erros
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            
            <Typography variant="body2" className={classes.importText} color="textSecondary">
              Por favor, não feche esta janela ou mude de página.
              <br />
              Você pode cancelar, mas os dados já processados serão mantidos.
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCancelarImportacao} 
            color="secondary"
            disabled={!importando}
          >
            Cancelar Importação
          </Button>
        </DialogActions>
      </Dialog>

      <ClienteFilterModal
        open={filterModalOpen}
        onClose={handleCloseFilterModal}
        onApplyFilters={handleApplyFilters}
        initialFilters={activeFilters}
      />
    </MainContainer>
  );
};

export default Clientes;
